// build-table.ts — pure builder for the class summary table.
//
// Client-side port of options/tables/table_class_summary_inc.php. That PHP calls query_full_item_arr()
// on EVERY ability/spell a class + subclass grant just to read item_name + requires_class_level, then
// prints a 40-row x N-column grid. We do the same read entirely from the cached blob: each AbilityType
// layer (and spell_known / curse_known) ships as bare-UUID refs that resolve to their FULL def via the
// blob's root `.catalog` (resolveNest), where requires_class_level + the name are readable. ZERO server
// queries. Markup mirrors the PHP (div.table-responsive-xxl > h5 header > table.table-striped... > 40 rows)
// so the result is visually identical; empty columns are dropped here (the PHP always printed all of them).

import { nameOf, mergeNamesWithCatalog, uuidSlot, type ReferencedNames } from '../list-display/helpers';
import { layerEntriesOf, layerOf, itemsCol, resolveNest } from '../shared/array-types';
import { ABILITY_TYPE_COLUMNS, SPELL_KNOWN_COLUMN, SPELL_KNOWN_LAYERS, ALL_COLUMNS, computeNonEmptyColumns } from './columns';
import { renderSearchBoxHtml } from './search';
import type { AbilityRow, AbilitySource } from './types';
import { classBlobOf, type ClassBlob } from './blob';

const MAX_LEVEL = 40;

/** Build the combined names map for a class blob + optional subclass blob (catalog + static registries). */
export function buildNames(classBlob: ClassBlob, subclassBlob: ClassBlob | null): ReferencedNames {
    let names = mergeNamesWithCatalog({}, classBlob);
    if (subclassBlob) names = mergeNamesWithCatalog(names, subclassBlob);
    return names;
}

/**
 * Resolve every ability/spell grant on one blob into table rows. Mirrors the PHP loop:
 * AbilityType layers → their own column; spell_known + curse_known → the 'spell_known' column.
 * An entry with no requires_class_level (or one outside 1..40) is omitted — exactly as the PHP, where
 * `requires_class_level == $x` never matches a null/out-of-range level for any row 1..40.
 */
export function collectAbilities(value: unknown, source: AbilitySource, names: ReferencedNames): AbilityRow[] {
    const rows: AbilityRow[] = [];
    const blob = classBlobOf(value);
    if (!blob) return rows;

    const push = (entry: unknown, column: string): void => {
        const node = resolveNest(entry, blob);
        const id = (uuidSlot(entry) || String(itemsCol(node, 'item_id') ?? '')).trim();
        if (!id) return;
        const rcl = layerOf(node, 'requires_class_level');
        if (rcl === undefined || rcl === null || rcl === '') return;
        const level = Math.trunc(Number(rcl));
        if (!Number.isFinite(level) || level < 1 || level > MAX_LEVEL) return;
        const name = nameOf(names, id) || String(itemsCol(node, 'item_name') ?? '');
        rows.push({ id, name, column, level, source });
    };

    for (const column of ABILITY_TYPE_COLUMNS) {
        for (const entry of layerEntriesOf(blob, column)) push(entry, column);
    }
    for (const layer of SPELL_KNOWN_LAYERS) {
        for (const entry of layerEntriesOf(blob, layer)) push(entry, SPELL_KNOWN_COLUMN);
    }
    return rows;
}

/** Sort by requires_class_level asc, then name (matches the PHP usort). Stable. */
function sortRows(rows: AbilityRow[]): AbilityRow[] {
    return rows
        .map((row, index) => ({ row, index }))
        .sort((a, b) => (a.row.level - b.row.level) || a.row.name.localeCompare(b.row.name) || (a.index - b.index))
        .map(x => x.row);
}

/** [level 1..40] -> [column] -> rows. */
function bucketByLevelAndColumn(rows: AbilityRow[]): Map<number, Map<string, AbilityRow[]>> {
    const buckets = new Map<number, Map<string, AbilityRow[]>>();
    for (const row of rows) {
        let byColumn = buckets.get(row.level);
        if (!byColumn) { byColumn = new Map(); buckets.set(row.level, byColumn); }
        const cell = byColumn.get(row.column);
        if (cell) cell.push(row); else byColumn.set(row.column, [row]);
    }
    return buckets;
}

function badgeHtml(row: AbilityRow): string {
    const color = row.source === 'class' ? 'primary' : 'secondary';
    const safeId = escapeHtml(row.id);
    return '<div><span class="badge bg-' + color + '" role="button"'
        + ' data-cs-name="' + escapeHtml(row.name.toLowerCase()) + '"'
        + ' onclick="openItemInfo(\'' + safeId + '\');">'
        + escapeHtml(row.name) + '</span></div>';
}

export interface RenderTableArgs {
    classId: string;
    className: string;
    rows: AbilityRow[];
    searchTerm: string;
}

/**
 * Render the table HTML: a gradient header bar (title left, spyglass search right) + thead + 40 rows.
 * Only non-empty columns get a th/td; the Level column is always present. Search filtering is applied at
 * the DOM layer afterward (index.ts wires the input + calls applySearch), so it does not affect this markup.
 */
export function renderClassTableHtml(args: RenderTableArgs): string {
    const sorted = sortRows(args.rows);
    const visible = computeNonEmptyColumns(sorted);
    const buckets = bucketByLevelAndColumn(sorted);

    let thead = '<th class="text-center">' + iconMap('level') + '</th>';
    for (const col of visible) thead += '<th class="text-center">' + iconMap(col) + '</th>';

    let tbody = '';
    for (let x = 1; x <= MAX_LEVEL; x++) {
        const byColumn = buckets.get(x);
        let tr = '<tr><td align="middle">' + x + '</td>';
        for (const col of visible) {
            const cell = byColumn ? byColumn.get(col) : undefined;
            tr += '<td>' + (cell ? cell.map(badgeHtml).join('') : '') + '</td>';
        }
        tbody += tr + '</tr>';
    }

    // Gradient header bar: class icon + name on the left, search aligned right (same bg-gradient idiom as
    // the site's other section headers — e.g. weapons / castable-spells h5s).
    const titleHtml = '<h5 class="ft-cs-title mb-0 d-flex align-items-center gap-2">'
        + iconMap(args.classId) + ' <span>' + escapeHtml(args.className) + ' Class Summary</span></h5>';
    // Stronger gradient than the default bg-gradient sheen: a brighter top highlight fading to a darker
    // base, layered over bg-primary.
    const headerBar = '<div class="ft-cs-header d-flex align-items-center justify-content-between gap-2 flex-wrap'
        + ' mt-1 mb-0 py-2 px-3 bg-primary text-light rounded-top border border-bottom-0"'
        + ' style="background-image: linear-gradient(180deg, rgba(255,255,255,0.30) 0%, rgba(255,255,255,0.04) 45%, rgba(0,0,0,0.28) 100%);">'
        + titleHtml + renderSearchBoxHtml(args.searchTerm) + '</div>';

    return '<div class="table-responsive-xxl">'
        + headerBar
        + '<table class="table table-striped table-sm table-hover table-bordered border">'
        + '<thead>' + thead + '</thead>'
        + '<tbody>' + tbody + '</tbody>'
        + '</table></div>';
}

// re-export for index.ts convenience
export { ALL_COLUMNS };
